import React, { useEffect, useState } from 'react';
import { Container } from 'react-bootstrap';
import DataTable from '../../components/DataTable/DataTable';
import { IColInfo } from '../../components/DataTable/Interfaces';
import { FormConfig } from '../../components/Fields/Types';
import useValidation from '../../components/Hooks/ValidationHook/useValidation';
import SpinnerOverlay from "../../components/Spinner/SpinnerOverlay";
import { ApplicationFunctions, useAuth } from '../../contexts/authContext';
import { useNotifications } from '../../contexts/notificationContext';
import EmployeeModel from '../../models/EmployeeModel';
import DataService from '../../services/data.service';
import Button from '../../components/Buttons/Button';
import ButtonBar from '../../components/Fields/ButtonBar';
import CheckboxField from '../../components/Fields/CheckboxField';
import Form from '../../components/Fields/Form';
import TextboxField from '../../components/Fields/TextboxField';
import Unauthorized from '../Authentication/Unauthorized';
import ActionMenu from '../../components/ActionMenu/ActionMenu';
import ActionMenuItem from '../../components/ActionMenu/ActionMenuItem';
import Employee_popup_CreateEmployee from './Popups/Employee_popup_CreateEmployee';
import { useNavigate } from 'react-router-dom';
import SwitchField from '../../components/Fields/SwitchField';

interface SearchCriteria {
    quickSearch: string
    active: boolean
    rememberFilter: boolean
}

const EmployeeSearchView: React.FC = () => {
    const [searchResult, setSearchResult] = useState<EmployeeModel[]>([]);
    const [showCreatePopup, setShowCreatePopup] = useState<boolean>(false);
    const [showSpinner, setShowSpinner] = useState<boolean>(false);
    const { handleException } = useNotifications();
    const navigate = useNavigate()
    const [formData, setFormData] = useState<SearchCriteria>({
        quickSearch: '',
        active: true,
        rememberFilter: false,
    });

    useEffect(() => {
        const filterItem = localStorage.getItem("employeeFilter");
        if (filterItem !== null) {
            let filter: SearchCriteria = JSON.parse(filterItem);
            setFormData(filter);
            search()
        }
    }, [])

    function search() {
        setShowSpinner(true);

        if (formData.rememberFilter)
            localStorage.setItem('employeeFilter', JSON.stringify(formData));
        else
            localStorage.removeItem(`employeeFilter`);

        DataService
            .post<EmployeeModel[]>(`api/v1/Employees/Search/`, formData)
            .then(response => {
                setShowSpinner(false);
                setSearchResult(response);
            })
            .catch(e => {
                setShowSpinner(false);
                handleException(e)
            });
    }

    function setDefaultCriteria() {
        let defaultCriteria = {
            quickSearch: '',
            active: true,
            rememberFilter: formData?.rememberFilter ?? false,
        }

        setFormData(defaultCriteria)
    }

    function onFieldChanged(name: string, value: string): void {
        formData[name] = value;
        setFormData(formData);

        console.log(name + ': ' + value);
    }

    function hideCreatePopup(id?: number) {
        setShowCreatePopup(false)
        if (id) {
            navigate('/plm/UserManagement/Employee/' + id)
        }
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'quickSearch', label: 'Search', labelSize: 2 },
            { name: 'active', label: 'Active employees only', labelSize: 2 },
            { name: 'rememberFilter', label: 'Remember search criteria', labelSize: 2 },

        ],
        handleSubmit: search,
        handleFieldChanged: onFieldChanged,
        showErrors: 'always',
    };

    const { getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData);

    const colInfos: IColInfo[] = [
        { title: 'Id', key: true, dataProp: 'id', dataType: 'number' },
        { title: 'Name', dataProp: 'user', dataType: 'user', sort: 'asc', filterInfo: { filterType: 'Textbox' } },
        { title: 'Department', dataProp: 'department.name', filterInfo: { filterType: 'CheckboxList' } },
    ];

    const { isAuthorizedFor } = useAuth();
    if (!isAuthorizedFor(ApplicationFunctions.ViewEmployee)) {
        return (<Unauthorized />);
    }

    const formActions =
        <ActionMenu title="Actions">
            <ActionMenuItem icon="fa fa-plus" iconVariant="success" title="Create new employee" width={220} onClick={() => { setShowCreatePopup(true); }} appFunction={ApplicationFunctions.CreateEmployee} />
        </ActionMenu>


    return (
        <div className='main-content'>
            <Form title='Employees' actions={formActions} footer={(<>
                <ButtonBar offset={2}>
                    <Button text='Search' {...getSubmitButtonProps()} />
                    <Button text='Clear' type='button' onClick={setDefaultCriteria} />
                </ButtonBar>
            </>)}>
                <Container fluid>
                    <TextboxField {...getFieldProps('quickSearch')} />
                    <SwitchField {...getFieldProps('active')} />
                    <SwitchField {...getFieldProps('rememberFilter')} />
                </Container>
            </Form>
            <div className="card" style={{ margin: 12 }}>
                <DataTable
                    name='dtEmplyees'
                    colInfos={colInfos}
                    tableData={searchResult}
                    navigateTitle='Employeer'
                    navigateUrl='/plm/UserManagement/Employee/'
                />
            </div>
            {showCreatePopup && <Employee_popup_CreateEmployee show={showCreatePopup} onHide={hideCreatePopup} />}
            {showSpinner && (<SpinnerOverlay />)}
        </div>
    );
}
export default EmployeeSearchView;